// App.js

// React core and hooks
import React, { useState, useCallback, useMemo, useEffect, memo, useRef, useReducer } from "react";

// Styling and themes
import "./App.css";
import { ThemeProvider } from "@mui/material/styles";
import { CustomSnackbar, CustomAlert } from "./CustomComponents";

// Material UI components and icons
import { Grid, Stack, Tooltip, Box, Typography } from "@mui/material";

// Internationalization
import { useTranslation } from "react-i18next";

// Custom components
import PropTypes from 'prop-types';
import WebPlayer from './WebPlayer'
import VolumeSlider from './PlayerCommands/VolumeSlider';
import { FullScreen } from "./PlayerCommands/FullScreen.js";
import PlayerCommands from "./PlayerCommands/PlayerCommands";
import PlayerCommands2 from "./PlayerCommands/PlayerCommands2";
import TabButtons from "./PlayerCommands/TabButtons.js";

// Context providers
import { useEnvironments, EnvironmentsProvider } from "./contexts/EnvironmentsContext.js";
import { UIProvider } from './Playlists/contexts/UIContext';
import { useSession, SessionProvider } from './contexts/SessionContext';
import { PlaylistsProvider, usePlaylists } from './contexts/PlaylistsContext';
import { PlaylistProvider } from './Playlists/contexts/PlaylistContext';

// Other utilities and components
import { currentTheme, selectTheme } from "./theme/ThemeUtils";
import { getUserId, isDemoAccount } from "./utils/Utils";
import { sendCommand } from "./wsTools";
import { useResponsive } from './utils/useResponsive';
import ShowMontages from "./SelectMontages/MontageSelection.js";
import Playlists from "./Playlists/playlists/PlayLists";
import TrialAccountSnackbar from "./utils/TrialAccountSnackBar.js";
import { CleanupTemporaryPlaylists } from "./Play/playModeUtils.js";
import useScreenPermission from './utils/useScreenPermission';
import BaseLine from "./theme/BaseLine";

// Accounts
import ActivateAccount from "./accounts/ActivateAccount";

// Error boundary components - kept for debugging purposes
import ErrorBoundary from './ErrorBoundary';
import { safeGet, safeArray } from './DebugUtils';
import { hasStaleAccountFlags, cleanupAccountFlags } from "./accounts/accountCleanup";

//Logging users
// import * as Sentry from "@sentry/react";



// Initialization Coordination Mechanism
// Overview
// This application uses a coordination mechanism (WallmuseInit) to ensure that the main app and web player components initialize in the correct sequence, preventing race conditions that could cause video playback issues.
// Key Components
// index.html
// Creates the global WallmuseInit object in the <head> section
// Tracks readiness of both player and webplayer components
// Provides methods for components to signal when they're ready
// Allows components to wait for other components to be ready
// index.js (Main App)
// Creates a fallback WallmuseInit if not found in HTML
// Signals when the main app is mounted and ready using WallmuseInit.ready('player')
// Includes a safety timeout to force ready state if initialization hangs
// App.js
// Accepts onAppReady callback from index.js
// Calls this callback after initial render to signal readiness
// Contains the WebPlayer component which coordinates with the main app
// WebPlayer Component
// Waits for the main player to be ready before proceeding with initialization
// Checks WallmuseInit._playerReady to determine if it needs to wait
// Uses WallmuseInit.playerReady() to wait for the player if necessary
// Signals its own readiness using WallmuseInit.ready('webplayer') after initialization
// Includes timeout and error handling to prevent deadlocks
// Flow Sequence
// HTML creates WallmuseInit (or index.js creates fallback)
// Main app initializes and signals readiness
// WebPlayer checks if main app is ready before proceeding
// WebPlayer completes initialization and signals its readiness
// Both components are ready, and any queued callbacks are processed
// Best Practices
// Always check if WallmuseInit exists before using it
// Signal component readiness even when errors occur to prevent deadlocks
// Use timeouts to ensure the system recovers if initialization hangs

// First, update the App function to accept and use the onAppReady prop

function App({ onAppReady }) {
  console.log("[App InitMechanism] started");
  // Add useEffect to signal app ready after initial render
  useEffect(() => {
    console.log("[App InitMechanism] Initial render complete, calling onAppReady");
    if (onAppReady && typeof onAppReady === 'function') {
      onAppReady();
    }
  }, [onAppReady]);

  return (
    <SessionProvider>
      <EnvironmentsProvider>
        <PlaylistsProvider>
          <div className="App">
            <DontStartBefore />
          </div>
        </PlaylistsProvider>
      </EnvironmentsProvider>
    </SessionProvider>

  );
}

export default App;

function DontStartBefore() {
  /** Non-rerenderable Initialisations */
  const volumeRef = useRef(parseInt(localStorage.getItem('wallmuse-volume') || '0'));
  const [isPlaying, setIsPlaying] = useState(false);
  const playModeRef = useRef(false);


  console.log("[DontStartBefore] started");

  // Initiate with necessary context data
  const {
    house,
    environments,
    currentPlaylist,
    setCurrentPlaylist,
    loading,
    setLoading,
    error,
    needsRefresh,
    setNeedsRefresh,
    resetRefreshNeeded
  } = useEnvironments();

  // All component state
  const [cleanupComplete, setCleanupComplete] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const { playlists, setPlaylists } = usePlaylists();

  const { t } = useTranslation();
  const theme = selectTheme();

  // Logging current state
  console.log(
    "house:", house,
    "environments.length:", environments?.length ?? 0,
    "currentPlaylist:", currentPlaylist,
    "loading:", loading
  );

  const userId = getUserId();
  const isDemo = isDemoAccount(userId);
  console.log('[DontStartBefore DEBUG] isDemoAccount(userId):', isDemoAccount(userId));


  // Use the screen permission hook
  const { checkPermission, PermissionDialog } = useScreenPermission({
    t: t,
    onStatusChange: (status) => {
      console.log("[DontStartBefore] Screen permission status changed:", status);
    }
  });

  // Check permission
  useEffect(() => {
    if (!isDemo) {
      checkPermission();
    } else {
      // For demo accounts, auto-complete all pre-requisites
      console.log("[DontStartBefore] Demo account - auto-completing prerequisites");
      setPermissionGranted(true);
      setCleanupComplete(true);
      // Any other initial states that should be skipped for demos
    }
  }, [checkPermission, isDemo]);

  // Only needed for non-demo accounts
  const handleCleanupComplete = useCallback(() => {
    if (!isDemo) {
      console.log("[DontStartBefore] Cleanup complete");
      setCleanupComplete(true);
    }
  }, [isDemo]);

  // Handle errors first
  if (error) {
    return <div className="error-message">{error}</div>;
  }

  // Ensure essential data is available
  if (!house || !environments || environments.length === 0) {
    return <div className="error-message">Environment data is missing. Please try reloading the page.</div>;
  }

  // Cleanup phase
  if (!cleanupComplete) {
    return (
      <CleanupTemporaryPlaylists
        currentPlaylist={currentPlaylist}
        setCurrentPlaylist={setCurrentPlaylist}
        house={house}
        onCleanupComplete={handleCleanupComplete}
      />
    );
  }

  if (isDemo && hasStaleAccountFlags()) {
    console.log('[DontStartBefore] Detected stale account flags on demo account, cleaning up');
    cleanupAccountFlags('demo-stale-flags', true);
  }

  // Ready to render the main app
  console.log("[DontStartBefore] Finished, rendering PlayerIntegration");
  return (
    <div className="PlayerIntegration">
      <PermissionDialog theme={theme} />
      <PlayerIntegration
        theme={theme}
        volumeRef={volumeRef}
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        playModeRef={playModeRef}
      />
    </div>
  );
}

// Utility function to send commands to the house
export const handleSendCommand = (command, house) => {
  if (!house) {
    console.error('[handleSendCommand] House ID is null, cannot send command.');
    return;
  }
  console.log('[App] Sending command:', command, 'to house:', house);
  sendCommand(house, command, (success, response) => {
    if (success) {
      console.log('[App] Command sent successfully:', response);
    } else {
      console.error('[App] Command failed:', response);
    }
  });
};

// Export the stop command with useCallback
export const useStopCommand = (house) => {
  return useCallback(() => {
    handleSendCommand('<vlc><cmd action="stop"/></vlc>', house);
  }, [house]);
};

let currentPlaylistPosition = 0; // ✅ Module-level variable

export const getCurrentPosition = () => {
    const videoElement = document.querySelector('.web-player-content video');
    if (videoElement && videoElement.currentTime > 0) {
        return currentPlaylistPosition; // ✅ Can access module variable
    }
    return 0;
};

export const updatePosition = (newPosition) => {
    currentPlaylistPosition = newPosition; // ✅ Can update module variable
    console.log(`[PLAYLIST_POSITION] Updated position to: ${newPosition}`);
};


// PlayerIntegration component
const PlayerIntegration = memo(function PlayerIntegration({ theme, volumeRef, isPlaying, setIsPlaying, playModeRef }) {
  // console.log("[App PlayerIntegration] Props received with volumeRef:", { house, environments, currentPlaylist, setCurrentPlaylist, volumeRef });

  const userId = getUserId();
  const { session } = useSession(); 
  const isDemo = isDemoAccount(userId);

// Re get all necessary context data
  const {
    house,
    environments,
    currentPlaylist,
    setCurrentPlaylist,
    loading,
    setLoading,
    error,
    needsRefresh,
    setNeedsRefresh,
    resetRefreshNeeded
  } = useEnvironments();

  const { playlists, setPlaylists } = usePlaylists();

  const { t } = useTranslation();

  const MIN_PLAYER_WIDTH = 320;
  const responsiveProps = useResponsive();

  const [playModeOneSec, setPlayModeOneSec] = useState(false);
  const [currentTempPlaylistId, setCurrentTempPlaylistId] = useState(null);

  // const isDemo = false; // Checks if the user is on a free/demo account
  const [accountCreatedSuccess, setAccountCreatedSuccess] = useState(false);
  const [accountSetupPhase, setAccountSetupPhase] = useState('initial');

  console.log('[App PlayerIntegration] isDemo, currentTheme(), theme, userId/session', isDemo, currentTheme(), theme,  userId);
  const { isMobile, isTablet, isHD, isUHD, isSmartTVHD, isSmartTVUHD } = responsiveProps;
  // Updated icon class logic with SmartTV considerations
  const iconClass = useMemo(() => {
    if (isMobile) return 'small-icon';
    if (isTablet) return 'tablet-icon';
    if (isHD) return 'hd-icon';
    if (isUHD) return 'uhd-icon';
    if (isSmartTVHD) return 'large-icon';  // Assuming larger icons for HD SmartTVs
    if (isSmartTVUHD) return 'xlarge-icon'; // Assuming even larger icons for 4K SmartTVs
    return ''; // default if none apply
  }, [isMobile, isTablet, isHD, isUHD, isSmartTVHD, isSmartTVUHD]);

  // At the beginning of your component, read the stored phase for account creation
  useEffect(() => {
    const storedPhase = localStorage.getItem('currentSetupPhase');
    if (storedPhase) {
      setAccountSetupPhase(storedPhase);
      setAccountCreatedSuccess(true);
      // Clear it after reading to avoid showing stale messages
      localStorage.removeItem('currentSetupPhase');
    }
  }, []);

  /** Play Mode */

  const handleTempPlaylistCreated = (playlistId) => {
    console.log("[App PlayerIntegration] Setting temp playlist ID:", playlistId);
    setCurrentTempPlaylistId(playlistId);
  };

  const previousPlaylist = useRef(null); // Stores the previous playlist to prevent redundant updates
  // console.log("[App TrackSelection] Playlists data:", playlists);

  // Get current screen ID - This assumes you know the current environment and screen
  const currentEnvironment = environments.find(env => env.alive === "1"); // Get active environment
  // console.log("[App TrackSelection] Current environment:", currentEnvironment);

  const currentScreenId = currentEnvironment?.screens?.[0]?.id; // Get first screen of active environment
  // console.log("[App TrackSelection] Current screen ID:", currentScreenId);

  // Function to handle starting Play Mode for a montage
  const handlePlayStart = () => {
    playModeRef.current = true;
    setPlayModeOneSec(true);    // Temporary 1-second visual effect
    setTimeout(() => setPlayModeOneSec(false), 1000); // Turn off after 1 second
  };

  // Function to handle ending Play Mode and switching back to Playlist Mode
  const handlePlayEnd = () => {
    playModeRef.current = false;
  };

  /** Generate safe montage order signature */

  // Use the safe utility functions to avoid errors
  console.log('Working with playlists:', playlists);

  // Use the safeArray utilities
  const currentPlaylistObj = safeArray.find(
    playlists,
    p => p.id === currentPlaylist
  );

  console.log('Current playlist object:', currentPlaylistObj);

  // Use safeGet for deeply nested properties
  const montages = safeGet(currentPlaylistObj, 'montages', []);

  // Generate the signature safely
  const montageOrderSignature = safeArray.map(
    montages,
    m => m.id
  ).join(',');

  console.log('Montage order signature:', montageOrderSignature);

  /** Track */

  // Get the selected track for current screen
  const selectedTrackForScreen = useMemo(() => {
    // console.log("[TrackSelection] Starting track selection with:", {
    //   hasPlaylists: !!playlists,
    //   playlistsCount: playlists?.length,
    //   currentPlaylist,
    //   currentScreenId
    // });

    if (!playlists || !currentScreenId) {
      // as currentPlaylist can be undefined, removed
      console.log("[App PlayerIntegration TrackSelection] Missing required data, defaulting to track 1 (playlists, currentScreenId)", playlists, currentPlaylist, currentScreenId);
      return '1'; // Default to track 1
    }

    // Find the current playlist
    const playlist = playlists.find(p => p.id === currentPlaylist);
    console.log("[App PlayerIntegrationTrackSelection] Found playlist:", playlist?.id, playlist?.name);

    if (!playlist) {
      console.log("[App PlayerIntegration TrackSelection] No matching playlist found, defaulting to track 1");
      return '1';
    }

    // Find the active montage
    const activeMontage = playlist.montages?.[0]; // Could be different logic to determine active montage
    console.log("[App PlayerIntegration TrackSelection] Active montage:", activeMontage?.id, activeMontage?.name);

    if (!activeMontage) {
      console.log("[App PlayerIntegration TrackSelection] No active montage found, defaulting to track 1");
      return '1';
    }

    // Find the screen in the montage
    const screenDetail = activeMontage.screens?.find(s => s.id === currentScreenId);
    console.log("[App PlayerIntegration TrackSelection] Screen detail for current screen:", screenDetail);

    // Return the seq value or default to '1'
    const trackToUse = screenDetail?.seq || '1';
    console.log("[App PlayerIntegration TrackSelection] Selected track:", trackToUse);
    return trackToUse;
  }, [playlists, currentPlaylist, currentScreenId]);

  console.log('[App PlayerIntegration] Selected track for screen:', selectedTrackForScreen, 'Screen ID:', currentScreenId);

  /** Player command functions */

  const onPlay = useCallback(() => handleSendCommand('<vlc><cmd action="play"/></vlc>', house));
  const onPause = useCallback(() => handleSendCommand('<vlc><cmd action="pause"/></vlc>', house));
  const onStop = useCallback(() => {
    handleSendCommand('<vlc><cmd action="stop"/></vlc>', house);
    setIsPlaying(false);
  });
  const onRew = useCallback(() => handleSendCommand('<vlc><cmd action="prev"/></vlc>', house));
  const onFwd = useCallback(() => handleSendCommand('<vlc><cmd action="next"/></vlc>', house));

  // Single volume change handler that does both jobs
  const onVolumeChange = useCallback((v) => {
    // Store the current volume value
    volumeRef.current = v;
    console.log('[PlayerIntegration onVolumeChange] Current volume:', volumeRef.current);
    // Send command to house
    handleSendCommand('<vlc><cmd action="volume" param="' + v + '"/></vlc>', house);
  }, [house, volumeRef]); // Only house is a dependency

  console.log('[PlayerIntegration] playModeRef', playModeRef);

  useEffect(() => {
    console.log("[App] useEffect: Refresh handling started");

    // Get initial state from localStorage
    const refreshShown = localStorage.getItem('refreshShown') === 'true';
    const needsRefresh = localStorage.getItem('needsRefresh') === 'true';
    const needsSecondRefresh = localStorage.getItem('needsSecondRefresh') === 'true';
    const refreshAttempts = parseInt(localStorage.getItem('refreshAttempts') || '0');
    const accountJustCreated = localStorage.getItem('accountJustCreated') === 'true';
    const activationInProgress = localStorage.getItem('activationInProgress') === 'true';
    const activationComplete = localStorage.getItem('activationComplete') === 'true';
    const guestUserId = localStorage.getItem('guestUserId');
    const wpSessionId = localStorage.getItem('wpSessionId');

    console.log("[App] Initial localStorage state:", {
      refreshShown,
      needsRefresh,
      needsSecondRefresh,
      refreshAttempts,
      accountJustCreated,
      activationInProgress,
      activationComplete,
      guestUserId,
      wpSessionId
    });

    // Add handler for account phase changes
    const handleAccountPhaseChange = (event) => {
      const { phase } = event.detail;
      console.log(`[App] Account phase change event: ${phase}`);

      setAccountSetupPhase(phase);
      setAccountCreatedSuccess(true);
    };

    // Add event listener for screen refresh
    const handleScreenNeedsRefresh = () => {
      console.log("[App] Screen needs refresh event received");
      setNeedsRefresh(true);
      localStorage.setItem('refreshShown', 'true');
      console.log("[App l.456] Set needsRefresh flag via event");
    };

    // Set up all event listeners
    window.addEventListener('account-phase-change', handleAccountPhaseChange);
    window.addEventListener('screen-needs-refresh', handleScreenNeedsRefresh);

    // Check for stored phase on load
    const storedPhase = localStorage.getItem('currentSetupPhase');
    if (storedPhase) {
      console.log(`[App] Found stored phase: ${storedPhase}`);
      setAccountSetupPhase(storedPhase);
      setAccountCreatedSuccess(true);
    }
    // Determine account setup phase - priority ordered for clarity
    else if (refreshAttempts >= 3) {
      // Error state - too many attempts
      console.log("[App] Setting phase: RETRY (too many refresh attempts)");
      setAccountSetupPhase('retry');
      setAccountCreatedSuccess(true);
    }
    else if (activationComplete && !needsRefresh && !needsSecondRefresh) {
      // Final completion state
      console.log("[App] Setting phase: COMPLETED (activation complete, no refreshes needed)");
      setAccountSetupPhase('completed');
      setAccountCreatedSuccess(true);

      // Clear any leftover flags
      localStorage.removeItem('activationInProgress');
      localStorage.removeItem('refreshAttempts');
      localStorage.removeItem('currentSetupPhase');
    }
    else if (activationComplete && refreshAttempts > 0 && refreshAttempts < 3) {
      // Post-refresh optimization state
      console.log("[App] Setting phase: OPTIMIZING (activation complete, still refreshing)");
      setAccountSetupPhase('optimizing');
      setAccountCreatedSuccess(true);
      localStorage.setItem('currentSetupPhase', 'optimizing');
    }
    else if (needsSecondRefresh) {
      // Second refresh state
      console.log("[App] Setting phase: FINALIZING (needs second refresh)");
      setAccountSetupPhase('finalizing');
      setAccountCreatedSuccess(true);
      localStorage.setItem('currentSetupPhase', 'finalizing');
    }
    else if (needsRefresh) {
      // First refresh state
      console.log("[App] Setting phase: SETUP (needs first refresh)");
      setAccountSetupPhase('setup');
      setAccountCreatedSuccess(true);
    }
    else if (activationInProgress) {
      // Account creation in progress
      console.log("[App] Setting phase: CREATING (activation in progress)");
      setAccountSetupPhase('creating');
      setAccountCreatedSuccess(true);
    }
    else if (accountJustCreated) {
      // Initial state
      console.log("[App] Setting phase: PREPARING (account just created)");
      setAccountSetupPhase('preparing');
      setAccountCreatedSuccess(true);
    }

    // Safety check for too many refresh attempts
    if (refreshAttempts > 3) {
      console.log("[App] Too many refresh attempts, stopping refresh cycle");
      // Clear all refresh-related flags
      localStorage.removeItem('needsRefresh');
      localStorage.removeItem('needsSecondRefresh');
      localStorage.removeItem('activationInProgress');
      localStorage.removeItem('refreshAttempts');
      localStorage.removeItem('refreshShown');
      localStorage.removeItem('currentSetupPhase');
      return;
    }

    // Handle first refresh (user-initiated)
    if (needsRefresh) {
      console.log("[App] needsRefresh flag detected, will show refresh dialog");
      setNeedsRefresh(true);
      localStorage.setItem('refreshShown', 'true');
      console.log("[App l.526] Updated needsRefresh state:", { shouldRefresh: true });
    }

    // Handle second refresh (automatic)
    if (needsSecondRefresh) {
      console.log("[App] needsSecondRefresh flag detected, will perform automatic refresh");

      // Increment attempts counter before performing refresh
      const newAttemptCount = refreshAttempts + 1;
      localStorage.setItem('refreshAttempts', newAttemptCount.toString());
      console.log(`[App l.536] Incremented refresh attempts to ${newAttemptCount}`);

      // Clear the second refresh flag to prevent immediate loops
      localStorage.removeItem('needsSecondRefresh');

      // If this is the final attempt, also clear other flags
      if (newAttemptCount >= 3) {
        console.log("[App] Final refresh attempt, clearing all refresh flags");
        localStorage.removeItem('needsRefresh');
        localStorage.removeItem('activationInProgress');
        localStorage.removeItem('refreshShown');
        localStorage.removeItem('currentSetupPhase');
      }

      // Use a longer delay for the refresh to ensure backend updates complete
      setTimeout(() => {
        console.log("[App] Executing automatic refresh");
        window.location.reload();
      }, 2000); // 2 seconds
    }

    // Cleanup function - must be at the end of useEffect
    return () => {
      window.removeEventListener('account-phase-change', handleAccountPhaseChange);
      window.removeEventListener('screen-needs-refresh', handleScreenNeedsRefresh);
    };
  }, [setNeedsRefresh, setAccountCreatedSuccess, setAccountSetupPhase]);

  // Function to get message based on the current phase
  const getAccountSetupMessage = () => {
    switch (accountSetupPhase) {
      case 'preparing':
        return t("status.account.preparing") || "Preparing your account...";
      case 'creating':
        return t("status.account.creating") || "Creating your personal account...";
      case 'setup':
        return t("status.account.setup") || "Setting up your environment...";
      case 'finalizing':
        return t("status.account.finalizing") || "Finalizing your account setup...";
      case 'optimizing':
        return t("status.account.optimizing") || "Optimizing your viewing experience...";
      case 'completed':
        return t("success.account.created") || "Your personal account has been created! You can now continue using the application.";
      case 'retry':
        return t("status.account.retry") || "Account setup needs attention. Please try refreshing the page.";
      default:
        return t("status.account.processing") || "Processing your account...";
    }
  };

  // Memoizing the component for performance optimization
  return useMemo(() => (
    <div style={{ minWidth: `${MIN_PLAYER_WIDTH}px` }}>
      <ErrorBoundary>
        <div className="App">
          <PlaylistProvider>
            <ThemeProvider theme={theme}>
              <BaseLine currentTheme={currentTheme} theme={theme} t={t} isDemo={isDemo}/>
              {needsRefresh && !isDemo && (
                <ErrorBoundary fallback={<div>There was an issue with account activation. Please refresh manually.</div>}>
                  <ActivateAccount currentTheme={currentTheme()} theme={theme} t={t} />
                </ErrorBoundary>
              )}

              {/* Message if demo account only */}
              {isDemo && <TrialAccountSnackbar isDemo={isDemo} theme={currentTheme()} />}
              <p>{"\n"}</p>

              {/* Buttons for selecting exhibitions, playlists, displays, and getting info */}
              <ErrorBoundary>
                <TabButtons
                  currentTheme={currentTheme}
                  t={t}
                  responsiveProps={responsiveProps}
                />
              </ErrorBoundary>

              <p>{"\n"}</p>
              <>
                {/* Web Player Container */}
                <Box
                  className="web-player-container"
                  sx={{
                    position: "relative",
                    width: "100%",
                    paddingTop: "56.25%", // Standard 16:9 aspect ratio
                    border: playModeRef.current ? `4px solid ${theme.palette.primary.main}` : "none",
                    borderRadius: playModeRef.current ? "4px" : "0",
                    overflow: "hidden",
                    backgroundColor: "black", // Consistent background
                    zIndex: 1,
                  }}
                >
                  {/* Player Content */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 2, // Higher than container
                    }}
                  >
                    {/* Play Mode Overlay - Using opacity transition instead of conditional rendering */}
                    {playModeRef.current && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 10,
                          left: 10,
                          zIndex: 3,
                          backgroundColor: "rgba(0,0,0,0.5)",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          opacity: playModeOneSec ? 1 : 0,
                          transition: "opacity 0.5s ease-in-out",
                        }}
                      >
                        <Typography variant="h6" sx={{ color: "white" }}>
                          Play Mode
                        </Typography>
                        {/* TODO Add Montage Name and Track */}
                      </Box>
                    )}

                    {/* Actual Web Player Component */}
                    <ErrorBoundary>
                      <WebPlayer
                        key={`web-player-${currentPlaylist}`} // Add a key to force proper remounting
                        currentTheme={currentTheme}
                        environments={environments}
                        currentPlaylist={currentPlaylist}
                        selectedTrack={selectedTrackForScreen}
                        t={t}
                        isPlaying={isPlaying}
                        setIsPlaying={setIsPlaying}
                        house={house}
                        volumeRef={volumeRef}
                        loading={loading}
                        setLoading={setLoading}
                        montageOrderSignature={montageOrderSignature}
                      />
                    </ErrorBoundary>
                  </Box>
                </Box>

                {/* Player commands */}
                <ErrorBoundary>
                  <PlayerCommands
                    onPlay={onPlay}
                    onPause={onPause}
                    onStop={onStop}
                    onRew={onRew}
                    onFwd={onFwd}
                    isPlaying={isPlaying}
                    setIsPlaying={setIsPlaying}
                    iconClass={iconClass}
                    t={t}
                    volumeRef={volumeRef}
                    onVolumeChange={onVolumeChange}
                    onPlayStart={handlePlayStart}
                    onPlayEnd={handlePlayEnd}
                    playModeRef={playModeRef}
                    currentPlaylist={currentPlaylist}
                    currentTempPlaylistId={currentTempPlaylistId}
                    house={house}
                    setPlaylists={setPlaylists}
                    responsiveProps={responsiveProps}
                  />
                </ErrorBoundary>

                {/* Player rating and social sharing */}
                <ErrorBoundary>
                  <PlayerCommands2
                    responsiveProps={responsiveProps}
                    iconClass={iconClass}
                  />
                </ErrorBoundary>
              </>

              {/* Montage selection todo:delay */}
              <UIProvider>
                <ErrorBoundary>
                  <div id="Playlists">
                    <Playlists
                      currentTheme={currentTheme}
                      house={house}
                      currentPlaylist={currentPlaylist}
                      setCurrentPlaylist={setCurrentPlaylist}
                      playlists={playlists}
                      setPlaylists={setPlaylists}
                      isDemo={isDemo}
                      playModeRef={playModeRef}
                      onPlayEnd={handlePlayEnd}
                    />
                  </div>
                </ErrorBoundary>
              </UIProvider>

              <ErrorBoundary>
                <div id="ShowMontages">
                  <ShowMontages
                    onStop={onStop}
                    onPlayStart={handlePlayStart}
                    onPlayEnd={handlePlayEnd}
                    onTempPlaylistCreated={handleTempPlaylistCreated}
                    playModeRef={playModeRef}
                  />
                </div>
              </ErrorBoundary>
              {/* Test button that uses Sentry's captureException */}
              {/* <button onClick={() => {
                console.log("Button clicked - sending test error");
                Sentry.captureException(new Error("Manual test error for Sentry!"));
              }}>
                Test Sentry Manual Capture
              </button> */}
              {/* TODO: complete with 1 preparing 2 activating 3 finalising */}
              {accountCreatedSuccess && (
                <CustomSnackbar
                  open={accountCreatedSuccess}
                  // Vary duration based on phase - longer for completed, shorter for in-progress phases
                  autoHideDuration={accountSetupPhase === 'completed' ? 6000 :
                    (accountSetupPhase === 'retry' ? 0 : 3000)}
                  onClose={() => setAccountCreatedSuccess(false)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                  <CustomAlert
                    // Use warning severity for retry phase
                    severity={accountSetupPhase === 'retry' ? "warning" : "success"}
                    onClose={() => setAccountCreatedSuccess(false)}
                  >
                    {getAccountSetupMessage()}
                  </CustomAlert>
                </CustomSnackbar>
              )}
            </ThemeProvider>
          </PlaylistProvider>
        </div>
      </ErrorBoundary>
    </div>
  ), [theme, t, onPlay, onPause, onStop, onRew, onFwd, onVolumeChange, iconClass, responsiveProps, currentPlaylist]);
});

// PropTypes definitions
App.propTypes = {
  onAppReady: PropTypes.func
};

PlayerIntegration.propTypes = {
  currentTheme: PropTypes.string,
  volumeRef: PropTypes.object.isRequired,
  isPlaying: PropTypes.bool.isRequired,
  setIsPlaying: PropTypes.func.isRequired,
  playModeRef: PropTypes.object.isRequired,
};